import React, {
    ForwardedRef,
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useState,
} from 'react';

import {
    AccessibilityInfo,
    Platform,
    TextInput,
    TouchableWithoutFeedback,
    View,
} from 'react-native';

import CloseIcon from 'assets/images/svgs/ico_close.svg';

import gStyles from 'config/styles';
import { PIN_CLOSE_BUTTON_ID } from 'config/testID';

import { PinCloseKey } from 'utils/hooks/useKeyboardCloseHandler';
import { isTablet, isWeb } from 'utils/responsiveness';

import SvgButton from 'components/CustomButtons/SvgButton';
import PinMask, {
    maxCharacters,
} from 'components/PinDialog/components/PinMask';
import getPinDialogText, {
    PinDialogStatus,
} from 'components/PinDialog/hooks/getPinDialogText';
import styles from 'components/PinDialog/styles';
import { PinDialogBodyRef, PinDialogProps } from 'components/PinDialog/types';
import Text, { ETextCategory, ETextColor } from 'components/Text';

import PinTimer from '../PinTimer';

function PinDialogBody(
    {
        onSubmit,
        onClose,
        onBodyPress,
        status,
        appState,
        haveToShowCloseButton,
    }: Omit<PinDialogProps, 'visible'> & {
        onBodyPress(): void;
    },
    ref: ForwardedRef<PinDialogBodyRef>,
) {
    const { title, description, testID } = getPinDialogText(status);
    const blocked = PinDialogStatus.BLOCKED === status;
    const [pin, setPin] = useState('');
    const inputRef = React.useRef<TextInput>(null);

    const onChangeText = useCallback((text: string) => {
        setPin(text.replace(/[^\d]/g, ''));
    }, []);

    const focusInput = useCallback((event) => {
        if (event.key === PinCloseKey.ESCAPE) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
        } else if (event.key !== 'Tab' && event.key !== 'Shift') {
            inputRef.current?.focus();
        }
    }, []);

    const handleInputFocus = useCallback(async () => {
        if (await AccessibilityInfo.isScreenReaderEnabled()) {
            // TextInput without importantForAccessibility or accessibilityElementsHidden pronounce accessibilityLabel twice
            AccessibilityInfo.announceForAccessibility(description);
        }
    }, [description]);

    useEffect(() => {
        if (pin.length === maxCharacters) {
            onSubmit(pin);
        }
    }, [pin]);

    useEffect(() => {
        if (isWeb) {
            window.addEventListener('keydown', focusInput);
        }

        return () => {
            if (isWeb) {
                window.removeEventListener('keydown', focusInput);
            }
        };
    }, []);

    useImperativeHandle(ref, () => ({
        clear() {
            setPin('');
            inputRef.current?.clear();
        },
        focus() {
            inputRef.current?.focus();
        },
        blur() {
            inputRef.current?.blur();
        },
    }));

    return (
        <>
            {haveToShowCloseButton && (
                <SvgButton
                    testID={PIN_CLOSE_BUTTON_ID}
                    Svg={CloseIcon}
                    style={styles.closeButton}
                    svgStyle={styles.closeIcon}
                    onPress={onClose}
                />
            )}
            <TouchableWithoutFeedback
                onPress={onBodyPress}
                style={gStyles.flex}
            >
                <View
                    style={[
                        gStyles.flex,
                        Platform.OS === 'web' && styles.webContent,
                        isTablet && styles.tabletContent,
                        blocked
                            ? styles.contentBlocked
                            : styles.contentNonBlocked,
                    ]}
                >
                    <Text
                        center
                        category={ETextCategory.title1}
                        color={ETextColor.neutral}
                        style={styles.title}
                        testID={testID}
                        {...(isWeb && { tabIndex: 0 })}
                    >
                        {title}
                    </Text>
                    <Text
                        center
                        category={ETextCategory.body1}
                        color={ETextColor.light_grey}
                        style={styles.description}
                        {...(isWeb && { tabIndex: 0 })}
                    >
                        {description}
                    </Text>
                    {blocked ? (
                        <PinTimer appState={appState} />
                    ) : (
                        <>
                            <TextInput
                                ref={inputRef}
                                value={pin}
                                autoFocus={Platform.OS !== 'android'}
                                keyboardType="number-pad"
                                maxLength={maxCharacters}
                                style={styles.hiddenInput}
                                onChangeText={onChangeText}
                                onFocus={handleInputFocus}
                                returnKeyType={Platform.select({
                                    ios: 'default',
                                    android: 'none',
                                })}
                                {...(isWeb && { tabIndex: 0 })}
                                accessibilityLabel={
                                    isWeb ? description : undefined
                                }
                                importantForAccessibility="no-hide-descendants"
                                accessibilityElementsHidden
                            />
                            <PinMask text={pin} />
                        </>
                    )}
                </View>
            </TouchableWithoutFeedback>
        </>
    );
}

export default forwardRef(PinDialogBody);
